import { css, html, LitElement, PropertyValues, unsafeCSS } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { Store } from "../state/store";
import { Controller } from "../state/controller";
import { sharedStyles } from "../styles/shared";
import { property } from "lit/decorators/property.js";
import {
	TOOLBOX_CATEGORIES,
	TOOLBOX_ITEMS_MAP,
	ToolboxCategory,
	ToolboxItem,
	ToolboxItemId,
	ToolboxTagKind
} from "../data/toolbox-items";
import { repeat } from "lit/directives/repeat.js";
import "../atoms/title";
import "../atoms/markdown";
import "../atoms/card";
import "../atoms/icon";
import "../atoms/form-item";
import "../atoms/seo/seo-title";
import "../atoms/chip";
import "../molecules/landing/landing-title-header";
import "../atoms/text";
import "../atoms/section";
import { ASSETS_PATHS, EMOJI_FONT_NAME, ROUTES } from "../config";
import { when } from "lit/directives/when.js";
import { ICON_CHEVRON_LEFT, ICON_COPY, ICON_LINK } from "../../icons";
import { sessionStore } from "../state/session/session-store";
import { copyToClipboard } from "../util/copy-paste-util";
import { get, translate } from "lit-translate";
import { languageStore } from "../state/language/language-store";
import { BsMarkdown } from "../atoms/markdown";
import { DirectiveResult } from "lit/async-directive";
import { translatedElement } from "../decorators/translated-element-decorator";

export async function getToolboxMarkdownContent(id: ToolboxItemId): Promise<string | undefined> {
	try {
		const res = await fetch(`${ASSETS_PATHS.toolbox(languageStore.currentLanguage)}/${id}.md`);

		// Make sure the result is markdown
		if (!["text/plain", "text/markdown"].includes(res.headers.get("content-type") ?? "")) {
			return undefined;
		}

		return res.text();
	} catch (err) {
		console.error(err);
		return undefined;
	}
}

function getTagData(kind: ToolboxTagKind): { emoji: string; name: string } {
	switch (kind) {
		case "energizer":
			return {
				name: get("Energizer"),
				emoji: "⚡"
			};
		case "opener":
			return {
				name: get("Opener"),
				emoji: "🔑"
			};
		case "closer":
			return {
				name: get("Closer"),
				emoji: "👋"
			};
		case "technique":
			return {
				name: get("Technique"),
				emoji: "⚙️"
			};
		case "tip":
			return {
				name: get("Tip"),
				emoji: "⭐️"
			};
	}
}

function getCategoryData(category: ToolboxCategory): { emoji: string; name: string } {
	switch (category) {
		case "engagement":
			return {
				emoji: "🚀",
				name: get("Engagement")
			};
		case "ideation":
			return {
				emoji: "💡",
				name: get("Ideation")
			};
		case "evaluation":
			return {
				emoji: "🎯",
				name: get("Evaluation")
			};
		case "preparation":
			return {
				emoji: "🛠️",
				name: get("Preparation")
			};
	}
}

@customElement("bs-markdown-with-title-emoji")
class BsMarkdownWithTitleEmoji extends BsMarkdown {
	static get styles() {
		return [
			...super.styles,
			css`
				h1::first-letter {
					font-size: var(--font-size-m);
					background: var(--background);
					border: 1px solid var(--shade-light);
					border-radius: var(--border-radius-l);
					font-family: "${unsafeCSS(EMOJI_FONT_NAME)}";
					float: left;
					margin-right: var(--space-s);
					padding: 3px 4px;
				}

				h1 {
					font-size: var(--markdown-h1-font-size, 1.15em);
				}
			`
		];
	}
}

class ToolboxStore extends Store {
	model$ = {
		sessionUser: sessionStore.model$.sessionUser,
		currentLanguage: languageStore.model$.currentLanguage
	};
}

@customElement("bs-toolbox")
@translatedElement
export class BsToolbox extends LitElement {
	static get styles() {
		return [
			sharedStyles,
			css`
				.emoji {
					font-family: "${unsafeCSS(EMOJI_FONT_NAME)}";
				}

				.category-emoji {
					font-size: var(--font-size-m);
					background: var(--background);
					border: 1px solid var(--shade-light);
					border-radius: var(--border-radius-l);
					width: 34px;
					height: 34px;
					display: flex;
					align-items: center;
					justify-content: center;
				}

				#categories {
					display: flex;
					flex-direction: column;
					gap: var(--space-xl);
				}

				.category-title {
					display: flex;
					align-items: center;
					gap: var(--space-s);
					margin: 0 0 var(--space-s);
				}

				.toolbox-item-tags {
					margin: 0 0 var(--space-s);
					line-height: 1;
					display: flex;
					flex-wrap: wrap;
					gap: var(--space-xs);
				}

				.toolbox-items {
					display: grid;
					grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
					gap: var(--space-m);
				}

				.toolbox-item {
					position: relative;
					overflow: hidden;
					height: 250px;
				}

				.toolbox-item-content,
				#toolbox-content {
					display: flex;
					height: 100%;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					text-align: center;
					position: relative;
					z-index: 1;
					background: rgba(0, 0, 0, 0.7);
					color: var(--light);
				}

				#toolbox-content-container,
				#hero-section {
					border-radius: var(--toolbox-border-radius, unset);
				}

				.toolbox-cover {
					position: absolute;
					left: 0;
					top: 0;
					object-fit: cover;
					width: 100%;
					height: 100%;
					transition: 120ms ease-out transform;
					transform: scale(1);
				}

				.toolbox-item:hover .toolbox-cover {
					transform: scale(1.05);
				}

				#hero-section {
					background: var(--shade-light);
				}

				#categories-section {
					--section-space-top: var(--space-xl);
				}

				#toolbox-content-container {
					overflow: hidden;
					width: 100%;
					height: 400px;
					position: relative;
				}

				#info-card {
					width: auto;
					display: inline-flex;
					align-items: flex-start;
					gap: var(--space-l);
					background: var(--shade-lighter);
					padding: var(--space-l) var(--space-xl);
					flex-wrap: wrap;
					flex-direction: row;
				}

				.info {
					display: flex;
					align-items: center;
					gap: var(--space-m);
					text-align: left;
				}

				#toolbox-content {
					gap: var(--space-l);
					padding: var(--space-l);
				}

				#content-section {
					--section-space-top: var(--space-xl);
				}

				#content-parts {
					display: flex;
					flex-direction: column;
					gap: var(--space-xl);
				}

				#back-button {
					position: absolute;
					top: var(--space-l);
					left: var(--space-l);
					z-index: 12;
				}

				#copy-link-button {
					position: absolute;
					right: var(--space-l);
					bottom: var(--space-l);
					--icon-size: 28px;
				}

				@media (max-width: 800px) {
					#hero-section {
						--title-font-size: var(--font-size-xl);
						--section-space: var(--space-xxl);
					}

					#categories-section,
					#content-section {
						--section-padding: var(--toolbox-section-padding-mobile, 0);
					}
				}
			`
		];
	}

	@property({ type: String, attribute: "selected-id" }) selectedId?: ToolboxItemId;
	@property({ type: Boolean, attribute: "show-back-button" }) showBackButton = false;

	protected toolboxItemContentMap: Map<ToolboxItemId, string> = new Map();
	protected controller = new Controller(this, new ToolboxStore(), {
		currentLanguage: (lan) => {
			if (this.selectedId != null) {
				this.cacheToolboxContent(this.selectedId);
			}
		}
	});

	selectToolboxItem(item: ToolboxItem | undefined) {
		this.selectedId = item?.id;
		this.dispatchEvent(new CustomEvent<ToolboxItem | undefined>("select", { detail: item }));
	}

	protected updated(props: PropertyValues) {
		super.updated(props);

		if (props.has("selectedId") && this.selectedId != null) {
			this.cacheToolboxContent(this.selectedId);
		}
	}

	async cacheToolboxContent(id: ToolboxItemId) {
		const markdown = await getToolboxMarkdownContent(id);
		if (markdown?.length) {
			this.toolboxItemContentMap.set(id, markdown);
			this.requestUpdate();
		}
	}

	renderToolboxInfo({
		emoji,
		title,
		label
	}: {
		emoji: string;
		title: string | DirectiveResult;
		label: string | DirectiveResult;
	}) {
		return html`
			<div class="info">
				<div class="emoji category-emoji">${emoji}</div>
				<div>
					<bs-text size="small">${label}</bs-text>
					<bs-title>${title}</bs-title>
				</div>
			</div>
		`;
	}

	copyLink(item: ToolboxItem) {
		copyToClipboard(`${location.origin}${ROUTES.toolbox(item.id)}`, {
			message: "The link was copied to your clipboard",
			icon: ICON_COPY,
			id: "copy-toolbox-link"
		});
	}

	renderToolboxContent(item: ToolboxItem) {
		const { sessionUser } = this.controller.model;
		const categoryData = getCategoryData(item.category);
		const contentMarkdown = this.toolboxItemContentMap.get(item.id);
		return html`
			<div id="toolbox-content-container">
				${when(
					this.showBackButton,
					() => html`
						<bs-form-item id="back-button" invert size="small" @click="${() => this.selectToolboxItem(undefined)}">
							<button slot="input">
								<bs-icon .icon="${ICON_CHEVRON_LEFT}"></bs-icon>
								<span>${translate("Toolbox")}</span>
							</button>
						</bs-form-item>
					`
				)}
				<div id="toolbox-content">
					<bs-seo-title level="1">
						<bs-title size="huge">${translate(/*@ignore*/ item.name)}</bs-title>
					</bs-seo-title>
					<bs-icon .icon="${ICON_LINK}" button id="copy-link-button" @click="${() => this.copyLink(item)}"></bs-icon>
					<bs-card id="info-card">
						${this.renderToolboxInfo({
							emoji: categoryData.emoji,
							label: translate("Category"),
							title: categoryData.name
						})}
						${repeat(item.tags ?? [], (tag) => {
							const { emoji, name } = getTagData(tag);
							return this.renderToolboxInfo({
								emoji,
								label: translate("Type"),
								title: name
							});
						})}
						${when(item.groupSize, () =>
							this.renderToolboxInfo({
								emoji: "👥",
								label: translate("Group Size"),
								title: item.groupSize!
							})
						)}
						${when(item.duration, () =>
							this.renderToolboxInfo({
								emoji: "⏱️",
								label: translate("Duration"),
								title: item.duration!
							})
						)}
						${when(item.ideamapSupport, () => {
							switch (item.ideamapSupport) {
								case "ai":
									return html`
										<bs-tooltip text="${item.name} is AI supported in Ideamap" align="bottom">
											${this.renderToolboxInfo({
												emoji: "🤖",
												label: "Ideamap AI",
												title: "Yes"
											})}
										</bs-tooltip>
									`;
							}

							return html`
								<bs-tooltip text="${translate("This tool is supported in Ideamap")}" align="bottom">
									${this.renderToolboxInfo({
										emoji: "✅",
										label: translate("Ideamap supported?"),
										title: translate("Yes")
									})}
								</bs-tooltip>
							`;
						})}
					</bs-card>
				</div>
				<img class="toolbox-cover" src="/assets/toolbox/${item.id}.webp" alt="Toolbox Illustration" loading="lazy" />
			</div>
			<bs-section id="content-section" width="thin">
				<div id="content-parts">
					${when(
						contentMarkdown,
						(markdown) => html`
							<bs-markdown-with-title-emoji size="large" text="${markdown}"></bs-markdown-with-title-emoji>
						`
					)}
					${when(sessionUser == null, () => {
						import("../atoms/blog-get-started-card");
						return html`
							<bs-blog-get-started-card
								text="${translate("Facilitate your next workshop with Ideamap")}"
								cta="${translate("Get started")} — ${translate("it's free")}"
							></bs-blog-get-started-card>
						`;
					})}
				</div>
			</bs-section>
		`;
	}

	renderToolboxCard(item: ToolboxItem) {
		return html`
			<bs-card class="toolbox-item" hoverable @click="${() => this.selectToolboxItem(item)}">
				<div class="toolbox-item-content">
					${when(
						item.tags?.length,
						() => html`
							<div class="toolbox-item-tags">
								${repeat(item.tags ?? [], (tag) => {
									const { emoji, name } = getTagData(tag);
									return html` <bs-chip><span class="emoji">${emoji}</span> ${name}</bs-chip>`;
								})}
							</div>
						`
					)}
					<bs-title size="large">${translate(/*@ignore*/ item.name)}</bs-title>
				</div>
				<img class="toolbox-cover" src="/assets/toolbox/${item.id}.webp" alt="Toolbox Illustration" loading="lazy" />
			</bs-card>
		`;
	}

	renderToolboxCategory(category: ToolboxCategory) {
		const { emoji, name } = getCategoryData(category);
		const items = TOOLBOX_ITEMS_MAP[category] ?? [];
		return html`
			<div>
				<div class="category-title">
					<div class="emoji category-emoji">${emoji}</div>
					<bs-title>${name}</bs-title>
				</div>
				<div class="toolbox-items">${repeat(items, this.renderToolboxCard.bind(this))}</div>
			</div>
		`;
	}

	render() {
		// If a specific item is selected, render it
		const selectedItem = Object.entries(TOOLBOX_ITEMS_MAP)
			.flatMap(([key, items]) => items)
			.find((item) => item.id === this.selectedId);
		if (selectedItem != null) return this.renderToolboxContent(selectedItem);

		return html`
			<bs-section id="hero-section" width="wide">
				<bs-landing-title-header
					headline-level="1"
					label="${translate("Facilitator Toolbox")}"
					headline="${translate("A collection of tools to help you facilitate better workshops.")}"
				></bs-landing-title-header>
			</bs-section>
			<bs-section id="categories-section">
				<div id="categories">
					${repeat(TOOLBOX_CATEGORIES, (category) => this.renderToolboxCategory(category as ToolboxCategory))}
				</div>
			</bs-section>
		`;
	}
}
